import { useEffect, useState } from "react";
import { Loader2, Save, Mail, RefreshCw, BellRing, FileDown, CalendarRange, MessageCircle, Send, ImageIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import api, { formatApiError } from "../api";

const statusBadge = {
  sent: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  skipped: "bg-slate-100 text-slate-700 border border-slate-200",
  failed: "bg-red-100 text-red-800 border border-red-200",
};
const statusLabel = { sent: "Terkirim", skipped: "Dilewati (tidak ada trip)", failed: "Gagal" };
const typeLabel = { otomatis: "Otomatis", manual: "Manual", laporan_bulanan: "Laporan Bulanan", laporan_bulanan_otomatis: "Laporan Bulanan (Otomatis)", wa_otomatis: "WhatsApp (Otomatis)", wa_manual: "WhatsApp (Manual)", wa_mingguan: "WhatsApp Mingguan (Otomatis)", wa_mingguan_manual: "WhatsApp Mingguan (Manual)", backup_email: "Backup Email (Manual)", backup_email_otomatis: "Backup Email (Mingguan)" };

const WhatsAppCard = ({ settings, setSettings }) => {
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingWeekly, setSendingWeekly] = useState(false);
  if (!settings) return null;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        alert_days: parseInt(settings.alert_days),
        notify_email: settings.notify_email,
        wa_target: settings.wa_target || "",
        wa_jam: settings.wa_jam || "21:00",
        wa_enabled: !!settings.wa_enabled,
      });
      setSettings(data);
      toast.success("Pengaturan WhatsApp disimpan");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/whatsapp/kirim");
      toast.success(`Ringkasan terkirim ke WhatsApp ${data.target}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const sendWeekly = async () => {
    setSendingWeekly(true);
    try {
      const { data } = await api.post("/whatsapp/kirim-mingguan");
      toast.success(`Rekap mingguan terkirim ke WhatsApp ${data.target}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSendingWeekly(false);
    }
  };

  return (
    <Card className="border-zinc-200 shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-emerald-700" /> Notifikasi WhatsApp
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-zinc-500">
          Ringkasan laba &amp; trip hari ini dikirim otomatis setiap malam, plus rekap mingguan tiap Senin 07:00 WIB (via Fonnte).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <div className="space-y-1.5">
            <Label>Nomor WhatsApp Tujuan</Label>
            <Input data-testid="setting-wa-target" inputMode="numeric" placeholder="08116157076"
              value={settings.wa_target || ""}
              onChange={(e) => setSettings({ ...settings, wa_target: e.target.value })} className="bg-white input-touch" />
          </div>
          <div className="space-y-1.5">
            <Label>Jam Kirim (WIB)</Label>
            <Input data-testid="setting-wa-jam" type="time" value={settings.wa_jam || "21:00"}
              onChange={(e) => setSettings({ ...settings, wa_jam: e.target.value })} className="bg-white input-touch" />
          </div>
          <div className="flex items-center gap-3 pb-2">
            <Switch data-testid="setting-wa-enabled" checked={!!settings.wa_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, wa_enabled: v })} />
            <span className="text-sm text-zinc-700">{settings.wa_enabled ? "Aktif" : "Nonaktif"}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button data-testid="save-wa-settings" type="button" onClick={save} disabled={saving}
            className="btn-touch bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Simpan
          </Button>
          <Button data-testid="send-wa-now" type="button" variant="outline" onClick={sendNow} disabled={sending}
            className="btn-touch border-emerald-200 text-emerald-700 hover:bg-emerald-50">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Kirim Sekarang (Uji)
          </Button>
          <Button data-testid="send-wa-weekly" type="button" variant="outline" onClick={sendWeekly} disabled={sendingWeekly}
            className="btn-touch border-sky-200 text-sky-700 hover:bg-sky-50">
            {sendingWeekly ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarRange className="w-4 h-4 mr-2" />} Kirim Rekap Mingguan (Uji)
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const prevMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const fmtTime = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function NotificationsPage() {
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [periode, setPeriode] = useState(prevMonth());
  const [sendingReport, setSendingReport] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [testing, setTesting] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);
  const [logoBusy, setLogoBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/logo", { responseType: "blob" })
      .then(() => setLogoVersion(Date.now()))
      .catch(() => setLogoVersion(0));
  }, []);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoVersion(Date.now());
      toast.success("Logo tersimpan — kini tampil di kop PDF invoice & surat jalan");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Gagal mengunggah logo");
    } finally {
      setLogoBusy(false);
    }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      await api.delete("/settings/logo");
      setLogoVersion(0);
      toast.success("Logo dihapus");
    } catch (err) {
      toast.error("Gagal menghapus logo");
    } finally {
      setLogoBusy(false);
    }
  };

  const sendTestEmail = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/test-email");
      toast.success(`Email uji terkirim ke ${data.to} dari "${data.from}"`, { duration: 7000 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail), { duration: 9000 });
    } finally {
      setTesting(false);
    }
  };

  const loadLogs = () => { api.get("/notifications").then(({ data }) => setLogs(data)).catch(() => setLogs([])); };

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
    loadLogs();
  }, []);

  const saveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        alert_days: parseInt(settings.alert_days),
        notify_email: settings.notify_email,
        sender_email: settings.sender_email,
        nama_perusahaan: settings.nama_perusahaan,
        alamat_perusahaan: settings.alamat_perusahaan,
        susut_toleransi: Object.fromEntries(
          Object.entries(settings.susut_toleransi).map(([k, v]) => [k, parseFloat(v)])
        ),
        upah_per_trip: parseFloat(settings.upah_per_trip),
        harga_komoditas: Object.fromEntries(
          Object.entries(settings.harga_komoditas).map(([k, v]) => [k, parseFloat(v)])
        ),
        scan_limit_harian: parseInt(settings.scan_limit_harian),
        scan_gagal_ambang: parseInt(settings.scan_gagal_ambang ?? 3),
        paket_otomatis: settings.paket_otomatis !== false,
      });
      setSettings(data);
      toast.success("Pengaturan notifikasi disimpan");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/notifications/send");
      if (data.status === "sent") toast.success(`Email terkirim ke ${data.recipient} (${data.count} trip)`);
      else toast.info("Tidak ada trip tertunda, email tidak dikirim");
      loadLogs();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const downloadReport = async () => {
    setDownloadingReport(true);
    try {
      const res = await api.get("/laporan/bulanan/pdf", { params: { periode }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Laporan_Bulanan_${periode}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Laporan bulanan PDF diunduh");
    } catch (err) {
      toast.error("Gagal mengunduh laporan bulanan");
    } finally {
      setDownloadingReport(false);
    }
  };

  const sendReport = async () => {
    setSendingReport(true);
    try {
      const { data } = await api.post(`/laporan/bulanan/kirim?periode=${periode}`);
      toast.success(`Laporan bulanan ${periode} terkirim ke ${data.recipient}`);
      loadLogs();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSendingReport(false);
    }
  };

  return (
    <div data-testid="notifications-page" className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900">Notifikasi</h1>
        <p className="text-sm text-zinc-500 mt-1">Pengaturan dan riwayat email peringatan trip "Berangkat" tertunda</p>
      </div>

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
            <BellRing className="w-4 h-4 text-emerald-700" /> Pengaturan
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!settings ? (
            <div className="flex items-center justify-center py-6 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (
            <form onSubmit={saveSettings} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>Ambang Hari (trip "Berangkat" lebih dari)</Label>
                  <Input data-testid="setting-alert-days" type="number" min="1" max="60" required value={settings.alert_days}
                    onChange={(e) => setSettings({ ...settings, alert_days: e.target.value })} className="bg-white tabular-nums" />
                </div>
                <div className="space-y-1.5">
                  <Label>Batas Scan Slip AI / pengguna / hari</Label>
                  <Input data-testid="setting-scan-limit" type="number" min="1" max="500" required value={settings.scan_limit_harian ?? 20}
                    onChange={(e) => setSettings({ ...settings, scan_limit_harian: e.target.value })} className="bg-white tabular-nums" />
                </div>
                <div className="space-y-1.5">
                  <Label>Ambang Gagal Scan → WA ke Owner (x/hari)</Label>
                  <Input data-testid="setting-scan-gagal-ambang" type="number" min="1" max="50" required value={settings.scan_gagal_ambang ?? 3}
                    onChange={(e) => setSettings({ ...settings, scan_gagal_ambang: e.target.value })} className="bg-white tabular-nums" />
                </div>
                <div className="space-y-1.5">
                  <Label>Paket Biaya Otomatis saat Trip "Berangkat"</Label>
                  <div className="flex items-center gap-3 h-10">
                    <Switch data-testid="setting-paket-otomatis" checked={settings.paket_otomatis !== false}
                      onCheckedChange={(v) => setSettings({ ...settings, paket_otomatis: v })} />
                    <span data-testid="setting-paket-otomatis-label" className="text-sm text-zinc-700">
                      {settings.paket_otomatis !== false ? "Aktif — paket komoditas langsung diterapkan" : "Nonaktif — terapkan manual dari Daftar Trip"}
                    </span>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Email Tujuan Notifikasi</Label>
                  <Input data-testid="setting-notify-email" type="email" required value={settings.notify_email}
                    onChange={(e) => setSettings({ ...settings, notify_email: e.target.value })} className="bg-white" />
                </div>
                <div className="space-y-1.5">
                  <Label>Email Pengirim (setelah verifikasi domain)</Label>
                  <Input data-testid="setting-sender-email" type="email" required value={settings.sender_email}
                    onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })} className="bg-white" />
                </div>
              </div>
              <div data-testid="resend-domain-guide" className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3 text-xs text-blue-900 space-y-1.5">
                <p className="font-semibold text-sm">Cara memakai domain email perusahaan (agar tidak dari onboarding@resend.dev):</p>
                <p>1. Buka <b>dashboard Resend → Domains → Add Domain</b>, masukkan domain perusahaan Anda.</p>
                <p>2. Tambahkan record DNS (TXT/DKIM/SPF) yang ditampilkan Resend di pengelola DNS domain Anda, tunggu status <b>Verified</b>.</p>
                <p>3. Isi kolom <b>Email Pengirim</b> di atas dengan alamat berdomain tersebut (mis. invoice@perusahaan.com), klik Simpan, lalu klik <b>Kirim Email Uji</b>.</p>
                <p className="text-blue-700/80">Email akan tampil sebagai: <b>{settings.nama_perusahaan ? `${settings.nama_perusahaan} <${settings.sender_email}>` : settings.sender_email}</b>. Selama domain belum verified, Resend hanya bisa mengirim ke email pemilik akun Resend.</p>
                <Button data-testid="test-email-button" type="button" variant="outline" size="sm" onClick={sendTestEmail} disabled={testing}
                  className="border-blue-300 text-blue-800 hover:bg-blue-100 mt-1 btn-touch bg-white">
                  {testing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Mail className="w-4 h-4 mr-1.5" />} Kirim Email Uji
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Nama Perusahaan (kop invoice &amp; dokumen)</Label>
                  <Input data-testid="setting-nama-perusahaan" value={settings.nama_perusahaan || ""}
                    onChange={(e) => setSettings({ ...settings, nama_perusahaan: e.target.value })} className="bg-white" />
                </div>
                <div className="space-y-1.5">
                  <Label>Alamat Perusahaan (baris bawah kop)</Label>
                  <Input data-testid="setting-alamat-perusahaan" value={settings.alamat_perusahaan || ""}
                    onChange={(e) => setSettings({ ...settings, alamat_perusahaan: e.target.value })} className="bg-white"
                    placeholder="Jl. ... , Kota, Telp/Email" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Logo Perusahaan (kop PDF invoice &amp; surat jalan — PNG/JPG maks 2 MB)</Label>
                <div className="flex items-center gap-3 flex-wrap">
                  {logoVersion > 0 && (
                    <img data-testid="logo-preview" src={`${process.env.REACT_APP_BACKEND_URL}/api/settings/logo?v=${logoVersion}`}
                      alt="Logo" className="h-14 w-auto border border-zinc-200 rounded-md bg-white p-1" />
                  )}
                  <input id="logo-file" data-testid="logo-file-input" type="file" accept="image/png,image/jpeg"
                    className="hidden" onChange={uploadLogo} />
                  <Button data-testid="logo-upload-button" type="button" variant="outline" disabled={logoBusy}
                    className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                    onClick={() => document.getElementById("logo-file").click()}>
                    {logoBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-2" />}
                    {logoVersion > 0 ? "Ganti Logo" : "Unggah Logo"}
                  </Button>
                  {logoVersion > 0 && (
                    <Button data-testid="logo-delete-button" type="button" variant="outline" disabled={logoBusy}
                      className="border-red-200 text-red-700 hover:bg-red-50" onClick={removeLogo}>
                      <Trash2 className="w-4 h-4 mr-2" /> Hapus
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Toleransi Penyusutan per Komoditas (KG) — baris trip melebihi angka ini ditandai merah</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {["CPO", "Palm Kernel", "Cangkang"].map((k) => (
                    <div key={k} className="space-y-1.5">
                      <Label className="text-xs text-zinc-500">{k}</Label>
                      <Input data-testid={`setting-toleransi-${k.replace(" ", "-")}`} type="number" min="0.01" step="0.01" required
                        value={settings.susut_toleransi?.[k] ?? ""}
                        onChange={(e) => setSettings({ ...settings, susut_toleransi: { ...settings.susut_toleransi, [k]: e.target.value } })}
                        className="bg-white tabular-nums" />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Pengaturan Penggajian — upah per trip &amp; harga komoditas untuk denda susut (Rp/KG)</Label>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-zinc-500">Upah per Trip Selesai (Rp)</Label>
                    <Input data-testid="setting-upah-per-trip" type="number" min="1" step="1" required
                      value={settings.upah_per_trip ?? ""}
                      onChange={(e) => setSettings({ ...settings, upah_per_trip: e.target.value })}
                      className="bg-white tabular-nums" />
                  </div>
                  {["CPO", "Palm Kernel", "Cangkang"].map((k) => (
                    <div key={k} className="space-y-1.5">
                      <Label className="text-xs text-zinc-500">Harga {k} (Rp/KG)</Label>
                      <Input data-testid={`setting-harga-${k.replace(" ", "-")}`} type="number" min="1" step="1" required
                        value={settings.harga_komoditas?.[k] ?? ""}
                        onChange={(e) => setSettings({ ...settings, harga_komoditas: { ...settings.harga_komoditas, [k]: e.target.value } })}
                        className="bg-white tabular-nums" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button data-testid="save-settings-button" type="submit" disabled={saving}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Simpan
                </Button>
                <Button data-testid="send-now-button" type="button" variant="outline" onClick={sendNow} disabled={sending}
                  className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 transition-colors duration-200">
                  {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />} Kirim Sekarang
                </Button>
              </div>
            </form>
          )}
          <p className="text-xs text-zinc-500 mt-4">
            Email otomatis dikirim maksimal 1x per hari selama masih ada trip "Berangkat" melebihi ambang hari.
            Mode testing Resend hanya bisa mengirim ke email akun Resend Anda (verifikasi domain untuk alamat lain).
          </p>
        </CardContent>
      </Card>

      <WhatsAppCard settings={settings} setSettings={setSettings} />

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
            <CalendarRange className="w-4 h-4 text-emerald-700" /> Laporan Bulanan ke Manajemen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="space-y-1.5">
              <Label>Periode Bulan</Label>
              <Input data-testid="laporan-periode-input" type="month" value={periode}
                onChange={(e) => setPeriode(e.target.value)} className="bg-white sm:w-44" />
            </div>
            <Button data-testid="laporan-download-button" type="button" variant="outline" onClick={downloadReport}
              disabled={downloadingReport || !periode}
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 transition-colors duration-200">
              {downloadingReport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />} Unduh PDF
            </Button>
            <Button data-testid="laporan-send-button" type="button" onClick={sendReport} disabled={sendingReport || !periode}
              className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200">
              {sendingReport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />} Kirim ke Email Manajemen
            </Button>
          </div>
          <p className="text-xs text-zinc-500 mt-4">
            Rekap PDF berisi ringkasan trip, tonase, penyusutan, penggajian supir, dan biaya servis armada.
            Laporan bulan sebelumnya juga dikirim <b>otomatis setiap tanggal 1</b> ke Email Tujuan Notifikasi di atas.
          </p>
        </CardContent>
      </Card>

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold tracking-tight">Riwayat Notifikasi</CardTitle>
          <Button data-testid="refresh-logs-button" variant="outline" size="sm" onClick={loadLogs} className="border-zinc-300">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Muat Ulang
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {logs === null ? (
            <div className="flex items-center justify-center py-12 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : logs.length === 0 ? (
            <p data-testid="logs-empty" className="px-5 py-10 text-center text-sm text-zinc-400">Belum ada riwayat notifikasi</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="notifications-table">
                <thead>
                  <tr className="border-y border-zinc-200 bg-zinc-50">
                    {["Waktu", "Tipe", "Status", "Jumlah Trip", "Penerima", "Keterangan"].map((h) => (
                      <th key={h} className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} data-testid={`log-row-${l.id}`} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200">
                      <td className="px-4 py-2.5 whitespace-nowrap">{fmtTime(l.sent_at)}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">{typeLabel[l.type] || l.type}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <Badge className={`${statusBadge[l.status] || ""} font-medium`}>{statusLabel[l.status] || l.status}</Badge>
                      </td>
                      <td className="px-4 py-2.5 tabular-nums">{l.count}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">{l.recipient}</td>
                      <td className="px-4 py-2.5 text-xs text-zinc-500 max-w-[220px] truncate">{l.error || l.email_id || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
